import * as THREE from 'three';
import type { TerrainRoot } from '../../terrain/TerrainRoot';
import type { IMapSurface, MapGeoPosition, MapHeightRevisionListener } from '../MapSurface';

export interface TiledTerrainSurfaceConfig {
    terrain: TerrainRoot;
    bounds?: { minLon: number; minLat: number; maxLon: number; maxLat: number };
    onHeightsChanged?: (listener: MapHeightRevisionListener) => () => void;
}

/** Adapter for streamed Web-Mercator terrain tiles. */
export class TiledTerrainSurface implements IMapSurface {
    private readonly terrain: TerrainRoot;
    private readonly bounds?: TiledTerrainSurfaceConfig['bounds'];
    private readonly subscribe?: TiledTerrainSurfaceConfig['onHeightsChanged'];

    constructor(config: TiledTerrainSurfaceConfig) {
        this.terrain = config.terrain;
        this.bounds = config.bounds;
        this.subscribe = config.onHeightsChanged;
    }

    get worldUnitsPerMeter(): number {
        return this.terrain.projection.unitsPerMeter;
    }

    geoToWorld(lat: number, lng: number, elevationMeters = 0): THREE.Vector3 | null {
        if (!this.containsGeo(lat, lng)) return null;
        const world = this.terrain.projection.geoToWorld(lng, lat, elevationMeters);
        return new THREE.Vector3(world.x, world.y, world.z);
    }

    worldToGeo(world: THREE.Vector3): MapGeoPosition | null {
        const geo = this.terrain.projection.worldToGeo(world.x, world.z);
        if (!this.containsGeo(geo.lat, geo.lon)) return null;
        const verticalScale = this.terrain.projection.unitsPerMeter
            * this.terrain.projection.verticalExaggeration;
        return {
            lat: geo.lat,
            lng: geo.lon,
            elevationMeters: verticalScale > 0 ? world.y / verticalScale : undefined,
        };
    }

    getWorldY(x: number, z: number): number | null {
        if (!this.containsWorld(x, z)) return null;
        return this.terrain.getWorldY(x, z);
    }

    containsWorld(x: number, z: number): boolean {
        const geo = this.terrain.projection.worldToGeo(x, z);
        return this.containsGeo(geo.lat, geo.lon);
    }

    onHeightsChanged(listener: MapHeightRevisionListener): () => void {
        return this.subscribe?.(listener) ?? (() => undefined);
    }

    private containsGeo(lat: number, lng: number): boolean {
        if (!this.bounds) return true;
        return lng >= this.bounds.minLon && lng <= this.bounds.maxLon
            && lat >= this.bounds.minLat && lat <= this.bounds.maxLat;
    }
}
